
import VueApp from '@/@types/app/VueApp';
import { Component, Prop } from 'vue-property-decorator';
import { ConnectorConfig, ConnectorStatus } from '@/models/ConnectorConfig';
import {
  dispatchLoadConnectors,
  dispatchTriggerSync,
  dispatchDisconnectConnector,
} from '@/store/dispatchers/connectorDispatchers';
import notificationService from '@/services/NotificationService';
import workspaceService from '@/services/workspace/WorkspaceService';
import { commitSetSelectedArticle } from '@/store/commits/workspaceViewCommits';
import { dispatchGetPersonalArticlesCount } from '@/store/dispatchers/uiDispatchers';
import navigationService from '@/services/NavigationService';
import urlService from '@/services/UrlService';
import DisconnectConfirmDialog from '@/components/connectors/DisconnectConfirmDialog.vue';

const SOURCE_TYPE_LABELS: Record<string, string> = {
  GITHUB: 'GitHub',
  BITBUCKET: 'Bitbucket',
  CONFLUENCE: 'Confluence',
  GDRIVE: 'Google Docs',
};

@Component({
  components: {
    DisconnectConfirmDialog,
  },
})
export default class ConnectedSourcesSection extends VueApp {
  @Prop({ required: true }) workspaceId!: string;

  syncingIds: Record<string, boolean> = {};
  disconnectingConnector: ConnectorConfig | null = null;
  isDisconnecting = false;

  get connectors(): ConnectorConfig[] {
    return (this.$store.state.connector?.connectors || []).filter(
      (c: ConnectorConfig) => c.status !== ConnectorStatus.DISCONNECTED && c.status !== ConnectorStatus.PENDING
    );
  }

  get isLoading(): boolean {
    return this.$store.state.connector?.isLoading || false;
  }

  mounted() {
    dispatchLoadConnectors(this.workspaceId);
  }

  sourceTypeLabel(connector: ConnectorConfig): string {
    return SOURCE_TYPE_LABELS[connector.sourceType] || connector.sourceType || 'Unknown';
  }

  sourceTypeClass(connector: ConnectorConfig): string {
    const type = (connector.sourceType || '').toLowerCase();
    return `connected-sources__source-type-badge--${type}`;
  }

  statusClass(connector: ConnectorConfig): string {
    const statusMap: Record<string, string> = {
      [ConnectorStatus.ACTIVE]: 'connected-sources__status-badge--active',
      [ConnectorStatus.ERROR]: 'connected-sources__status-badge--error',
      [ConnectorStatus.PENDING]: 'connected-sources__status-badge--pending',
      [ConnectorStatus.PAUSED]: 'connected-sources__status-badge--paused',
      [ConnectorStatus.DISCONNECTING]: 'connected-sources__status-badge--paused',
    };
    return statusMap[connector.status] || '';
  }

  createdByName(connector: ConnectorConfig): string {
    const user = connector.createdByUser;
    if (!user) return '';
    if (user.firstName || user.lastName) {
      return [user.firstName, user.lastName].filter(Boolean).join(' ');
    }
    return user.email || '';
  }

  lastSyncLabel(connector: ConnectorConfig): string {
    if (!connector.lastSyncAt) return 'Never synced';
    return this.timeAgo(connector.lastSyncAt);
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleString();
  }

  timeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDays = Math.floor(diffHr / 24);
    return `${diffDays}d ago`;
  }

  async handleSyncNow(connector: ConnectorConfig) {
    if (!connector.id) return;
    this.$set(this.syncingIds, connector.id, true);
    try {
      await dispatchTriggerSync(connector.id);
      notificationService.success(`Sync triggered for ${connector.name}`);
      await dispatchLoadConnectors(this.workspaceId);
    } catch {
      notificationService.error(`Failed to sync ${connector.name}`);
    } finally {
      this.$set(this.syncingIds, connector.id, false);
    }
  }

  handleDisconnectClick(connector: ConnectorConfig) {
    this.disconnectingConnector = connector;
  }

  handleDisconnectCancel() {
    this.disconnectingConnector = null;
  }

  async handleDisconnectConfirm(keepArticles: boolean) {
    const connector = this.disconnectingConnector;
    if (!connector?.id) return;

    this.isDisconnecting = true;
    try {
      const result = await dispatchDisconnectConnector(connector.id, keepArticles);
      if (result?.workspaceOrder) {
        this.$store.commit('setWorkspaceOrder', result.workspaceOrder);
      }
      await workspaceService.loadArticlesItems();
      await dispatchGetPersonalArticlesCount();
      if (!keepArticles) {
        commitSetSelectedArticle(null);
        const workspace = this.$store.state.workspaceView?.workspace;
        if (workspace) {
          await navigationService.navigate(urlService.getWorkspaceViewRoute(workspace));
        }
      }
      notificationService.success(`${connector.name} disconnected`);
    } catch {
      notificationService.error(`Failed to disconnect ${connector.name}`);
    } finally {
      this.isDisconnecting = false;
      this.disconnectingConnector = null;
    }
  }
}
